import { CartUpdatedEvent, ThemeEvents } from '@theme/events';
import { offerTotal, matchingPack, bundleCartItem } from '@theme/optic-bundle-core';

class OpticBundle extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.form = this.closest('form');
    if (!this.form) return;
    this.button = this.querySelector('[data-bundle-submit]');
    this.error = this.querySelector('[data-bundle-error]');
    this.offers = [...this.querySelectorAll('[data-bundle-offer]')];
    try {
      this.packs = JSON.parse(this.querySelector('[data-bundle-packs]')?.textContent || '[]');
    } catch {
      this.packs = [];
    }
    this.variant = { id: this.dataset.variantId, price: Number(this.dataset.price), compare_at_price: Number(this.dataset.compare), available: this.dataset.available === 'true' };
    this.money = new Intl.NumberFormat(this.dataset.locale || 'fr', { style: 'currency', currency: this.dataset.currency || 'EUR' });
    this.addEventListener('change', event => {
      if (event.target.matches('.optic-bundle__radio')) this.update();
    }, options);
    this.form.addEventListener('submit', event => {
      event.preventDefault();
      event.stopImmediatePropagation();
      void this.submit();
    }, { ...options, capture: true });
    this.closest('product-form')?.addEventListener(ThemeEvents.variantSelected, () => {
      this.variantPending = true;
      this.button.disabled = true;
    }, options);
    this.closest('product-form')?.addEventListener(ThemeEvents.variantUpdated, event => {
      if (event.detail?.data?.productId && String(event.detail.data.productId) !== this.dataset.productId) return;
      this.variantPending = false;
      this.variant = event.detail.resource || { available: false };
      this.update();
    }, options);
    document.addEventListener('shopify:block:select', event => {
      const offer = event.target.closest?.('[data-bundle-offer]') || event.target.querySelector?.('[data-bundle-offer]');
      if (offer && this.contains(offer)) {
        offer.querySelector('.optic-bundle__radio').checked = true;
        this.update();
      }
    }, options);
    const requestedPack = Number(new URLSearchParams(window.location.search).get('lp_pack'));
    const initialIndex = Number.isInteger(requestedPack) && requestedPack >= 1 && requestedPack <= this.offers.length
      ? requestedPack - 1
      : Number(this.dataset.defaultOffer) - 1;
    const initial = this.offers[initialIndex] || this.offers[0];
    if (initial) initial.querySelector('.optic-bundle__radio').checked = true;
    this.style.setProperty('--ob-offer-count', String(this.offers.length));
    this.dataset.ready = '';
    this.form.dataset.opticBundleActive = 'true';
    this.update();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    if (this.form && !this.form.querySelector('optic-bundle')) delete this.form.dataset.opticBundleActive;
  }

  readOffer(element) {
    const quantity = Number(element.dataset.quantity);
    const discount = Number(element.dataset.discount);
    const gifts = [...element.querySelectorAll('[data-bundle-gift]')].map(gift => ({
      variantId: gift.dataset.variantId, quantity: Number(gift.dataset.quantity) * (gift.dataset.perCamera === 'true' ? quantity : 1), available: gift.dataset.available === 'true',
    }));
    const offer = { variantId: this.variant.id, quantity, discount, gifts, total: offerTotal(this.variant.price, quantity, discount) };
    // Conversion rounds each native parent independently. Shopify's contextual
    // parent price is the amount charged at checkout in the selected currency.
    const isConverted = this.dataset.currency !== this.dataset.shopCurrency;
    const pack = matchingPack(this.packs, offer, isConverted);
    if (pack) offer.total = Number(pack.price);
    return offer;
  }

  update() {
    let selected = null;
    for (const element of this.offers) {
      const offer = this.readOffer(element);
      element.querySelectorAll('[data-bundle-price]').forEach(node => { node.textContent = this.money.format(offer.total / 100); });
      for (const node of element.querySelectorAll('[data-bundle-unit]')) node.textContent = this.money.format(offer.total / offer.quantity / 100);
      if (this.dataset.locale === 'en-GB') element.querySelectorAll('[data-bundle-zero]').forEach(node => { node.textContent = this.money.format(0); });
      let giftCount = 0;
      const giftElements = element.querySelectorAll('[data-bundle-gift]');
      for (const [index, gift] of offer.gifts.entries()) {
        giftCount += gift.quantity;
        const label = giftElements[index]?.querySelector('[data-gift-quantity]');
        if (label) label.textContent = `×${gift.quantity}`;
      }
      const counter = element.querySelector('[data-gift-count]');
      if (counter) counter.textContent = this.dataset.countTemplate.replace('[count]', String(giftCount));
      const comparison = Number(this.variant.compare_at_price || this.variant.price) * offer.quantity;
      element.querySelectorAll('[data-bundle-compare]').forEach(node => {
        node.textContent = this.money.format(comparison / 100);
        node.hidden = comparison <= offer.total;
      });
      const image = this.variant.featured_image;
      if (image && element.dataset.variantImage === 'true') {
        const img = element.querySelector('.optic-bundle__image');
        if (img) { img.src = typeof image === 'string' ? image : image.src; img.removeAttribute('srcset'); }
      }
      if (element.querySelector('.optic-bundle__radio').checked) selected = offer;
    }
    this.selected = selected;
    this.pack = selected ? matchingPack(this.packs, selected) : null;
    let message = '';
    if (!this.variant.available) message = this.dataset.unavailable;
    else if (selected?.gifts.some(gift => !gift.available || !gift.variantId)) message = this.dataset.giftUnavailable;
    else if (!this.pack?.available) message = this.dataset.notReady;
    this.button.disabled = Boolean(message) || Boolean(this.busy) || Boolean(this.variantPending);
    this.error.hidden = !message;
    this.error.textContent = message;
    this.setButtonLabel(this.busy ? this.dataset.busyLabel : this.dataset.label);
    this.button.setAttribute('aria-busy', String(Boolean(this.busy)));
    if (selected) {
      const price = this.money.format(selected.total / 100);
      this.querySelector('[data-selected-price]').textContent = price;
      const giftCount = selected.gifts.reduce((sum, gift) => sum + gift.quantity, 0);
      const announcement = this.dataset.selectionTemplate.replace('[cameras]', selected.quantity).replace('[gifts]', giftCount).replace('[price]', price);
      const liveRegion = this.querySelector('[data-bundle-announcement]');
      if (liveRegion.textContent !== announcement) liveRegion.textContent = announcement;
    }
  }

  setButtonLabel(label) {
    const node = this.button.querySelector('[data-bundle-button-label]');
    if (node) node.textContent = label;
  }

  async submit() {
    if (this.busy || this.button.disabled || !this.pack) return;
    this.busy = true;
    this.update();
    let added = false;
    const root = window.Shopify?.routes?.root || '/';
    try {
      const properties = {};
      for (const [key, value] of new FormData(this.form)) {
        const match = key.match(/^properties\[([^\]]+)\]$/);
        if (match && typeof value === 'string') properties[match[1]] = value;
      }
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.dataset.sectionId ? [drawer.dataset.sectionId] : [];
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [bundleCartItem(this.pack, properties)], sections, sections_url: window.location.pathname }),
      });
      const data = await response.json();
      if (!response.ok || data.status >= 400) throw new Error(typeof data.description === 'string' ? data.description : this.dataset.error);
      added = true;
      if (!drawer || this.dataset.afterAdd === 'cart' || !data.sections?.[drawer.dataset.sectionId]) {
        window.location.assign(`${root}cart`);
        return;
      }
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!cartResponse.ok) throw new Error('Cart refresh failed');
      const cart = await cartResponse.json();
      document.dispatchEvent(new CartUpdatedEvent(cart, { source: 'optic-bundle', itemCount: cart.item_count, sections: data.sections }));
      document.dispatchEvent(new CustomEvent(ThemeEvents.cartOpen));
    } catch (error) {
      if (added) {
        // An add succeeded but the drawer refresh failed. Never invite another add.
        window.location.assign(`${root}cart`);
        return;
      }
      this.error.textContent = error.message || this.dataset.error;
      this.error.hidden = false;
    } finally {
      this.busy = false;
      this.button.disabled = !this.pack?.available || Boolean(this.variantPending);
      this.button.setAttribute('aria-busy', 'false');
      this.setButtonLabel(this.dataset.label);
    }
  }
}
if (!customElements.get('optic-bundle')) customElements.define('optic-bundle', OpticBundle);
